import { CategoryInfo, RegisteredTable } from "@/apis/workbook/types";
import DLSection from "./dl-section";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

const RoleSectionExpandable = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col items-start gap-6 self-stretch">
      <div className="group w-full flex p-2 justify-center items-center gap-2.5 self-stretch bg-white">
        <span className="title-3-bold overflow-hidden text-ellipsis whitespace-nowrap text-start flex-1 text-primary">
          {title}
        </span>
      </div>
      <div className="flex flex-col w-full gap-10">{children}</div>
    </div>
  );
};

const RoleSection = forwardRef<HTMLDivElement, { category: CategoryInfo }>(
  ({ category }, ref) => {
    const sortedTables = useMemo(
      () =>
        [...(category.tables || [])].sort((a, b) =>
          a.name.localeCompare(b.name, "ko", { numeric: true }),
        ),
      [category.tables],
    );

    const [selectedTable, setSelectedTable] = useState<RegisteredTable | null>(
      sortedTables[0] || null,
    );

    useEffect(() => {
      if (sortedTables.length > 0) {
        setSelectedTable(sortedTables[0]);
      }
    }, [category.id]);

    return (
      <div className="w-full mb-8" id={category.id} ref={ref}>
        <RoleSectionExpandable title={category.category}>
          <div className="flex items-start content-start self-stretch flex-wrap">
            {sortedTables.map((table) => (
              <button
                className={cn(
                  "flex w-[232px] py-4 px-3 justify-center items-center content-center gap-2.5 flex-wrap border border-neutral-300 cursor-pointer transition-colors duration-100 -mr-px",
                  selectedTable?.id === table.id
                    ? "bg-neutral-950 text-white border-neutral-950"
                    : "bg-white text-primary hover:bg-neutral-100 active:bg-neutral-200",
                )}
                onClick={() => setSelectedTable(table)}
                key={table.id}
              >
                <span className="headline-1-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                  {table.name}
                </span>
              </button>
            ))}
          </div>
          {selectedTable && (
            <DLSection
              table={selectedTable}
              categoryPreLabel={category.preRole.label}
            />
          )}
        </RoleSectionExpandable>
      </div>
    );
  },
);

RoleSection.displayName = "RoleSection";

export default RoleSection;
